import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { INTL_DAY_MONTH_NAME_SHORT } from 'constants/dateFormats'
import LineCalendarIcon from 'components/Luxkit/Icons/line/LineCalendarIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import OfferFilterSelectBox from '../OfferFilterSelectBox'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { fireInteractionEvent } from 'api/googleTagManager'
import { CALENDAR_GA_EVENT_ACTION, CALENDAR_GA_EVENT_LABEL } from 'constants/analytics'
import { calendarAction } from 'analytics/eventDefinitions'
import { rem } from 'polished'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { sum } from 'lib/arrayUtils'
import { pluralizeToString } from 'lib/pluralize'
import { useIntlDateFormatter } from 'lib/dateUtils'

const SizedFilterSelectBox = styled(OfferFilterSelectBox)`
  ${mediaQueryUp.md} {
    min-width: ${rem(250)};
  }
`

interface Props {
  bundledOffersState: OfferPageState['bundledOffers'];
  checkIn: moment.Moment;
  selectedDuration: number;
  activeFilter: string;
  offerType: App.OfferType;
  onClickLabel: string;
  inSidebar?: boolean;
}

function OfferCalendarDatePickerSelectBox(props: Props) {
  const {
    checkIn,
    selectedDuration,
    activeFilter,
    offerType,
    onClickLabel,
    inSidebar,
    bundledOffersState,
  } = props

  const bundledOffers = useMemo(() => Object.values(bundledOffersState ?? {}).filter(Boolean), [bundledOffersState])

  const intlDateFormatter = useIntlDateFormatter()

  let dates = useMemo(() => [{
    checkIn,
    checkOut: checkIn?.clone()?.add(selectedDuration, 'days'),
  }], [checkIn, selectedDuration])

  let duration = selectedDuration

  if (bundledOffers.length > 0) {
    dates = bundledOffers.map((bundledOffer) => ({
      checkIn: bundledOffer.checkInDate,
      checkOut: bundledOffer.checkInDate?.clone()?.add(bundledOffer.duration, 'days'),
    }))

    duration = sum(bundledOffers.filter((d) => d.checkInDate), (d) => d.duration)
  }

  const value = useMemo(() => {
    const validDates = dates.filter((d) => d.checkIn)
    if (validDates.length) {
      const dateText = validDates.map((d) => `${intlDateFormatter(d.checkIn, INTL_DAY_MONTH_NAME_SHORT)} - ${intlDateFormatter(d.checkOut, INTL_DAY_MONTH_NAME_SHORT)}`).join(', ')
      const roomText = pluralizeToString('night', duration)
      return <><b>{dateText}</b> ({roomText})</>
    }
  }, [dates, duration, intlDateFormatter])

  const dispatch = useOfferPageDispatch()
  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const isOpen = activeFilter === 'checkin'

  const onClick = useCallback(() => {
    if (isOpen) {
      fireInteractionEvent(calendarAction(offerType, CALENDAR_GA_EVENT_ACTION.CloseFilter, CALENDAR_GA_EVENT_LABEL.StickyBar))
      dispatch({ type: OfferPageActions.closeFilter })
      trackEvent('CALENDAR_CLOSED')
    } else {
      fireInteractionEvent(calendarAction(offerType, CALENDAR_GA_EVENT_ACTION.OpenFilter, onClickLabel))
      dispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      trackEvent('CALENDAR_TOGGLE_PRESSED')
    }
  }, [dispatch, isOpen, offerType, onClickLabel, trackEvent])

  return (
    <SizedFilterSelectBox
      inSidebar={inSidebar}
      onClick={onClick}
      active={isOpen}
      icon={<LineCalendarIcon />}
      data-testid="OfferCalendarDatePickerSelectBox"
      label="When"
      placeholder="Select your dates"
      value={value}
    />
  )
}

export default withOfferPageState((state) => {
  return {
    bundledOffersState: state.bundledOffers,
    checkIn: state.checkInDate,
    selectedDuration: state.duration,
    activeFilter: state.activeFilter,
    offerType: state.offer.analyticsType,
  }
})(OfferCalendarDatePickerSelectBox)
